import Cookies from 'js-cookie'
import { cookiePreference } from './preferences'
import type { CookiePreferenceName, CookiePreferenceOption, CookiePreferences } from './preferences'
import { useCookieContext } from '~/components/Cookies/CookieContextProvider/CookieContextProvider'
import type { CookieContextValue } from '~/components/Cookies/CookieContextProvider/CookieContextProvider'
import type { Nullable } from '../utils'

const consentCookieName = 'cookie_consent'
const cookieExpiresInDays = 365

type ConsentValue = 'granted' | 'denied'
type ConsentCookie = Record<string, ConsentValue>

const consentKey = (name: CookiePreferenceName): string => `${name}Cookies`

const readConsentCookie = (): Nullable<ConsentCookie> => {
  const raw = Cookies.get(consentCookieName)

  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as ConsentCookie
  } catch {
    return null
  }
}

const writeConsentCookie = (consent: ConsentCookie): void => {
  Cookies.set(consentCookieName, JSON.stringify(consent), { expires: cookieExpiresInDays })
}

const removeConsentCookie = (): void => {
  Cookies.remove(consentCookieName)
}

const consentToPreferences = (consent: Nullable<ConsentCookie>): CookiePreferences => {
  const entries = Object.values(cookiePreference).map((value) => {
    const pref = value as CookiePreferenceOption

    return [pref.name, consent?.[consentKey(pref.name)] === 'granted']
  })

  return Object.fromEntries(entries)
}

const getPreference = (name: CookiePreferenceName): Nullable<CookiePreferenceOption> => {
  const preference = cookiePreference[name]

  if (!preference) {
    return null
  }

  return preference
}

const hasPreference = (preference: CookiePreferenceOption): boolean => {
  if (!preference) {
    return false
  }

  if (preference.isNecessary) {
    return true
  }

  return readConsentCookie()?.[consentKey(preference.name)] === 'granted'
}

const setPreference = (
  context: CookieContextValue,
  name: CookiePreferenceName,
  value: boolean,
  force = false
): void => {
  const preference = getPreference(name)

  if (!preference) {
    return
  }

  const set = force ? value : preference.isNecessary || value

  const consent = readConsentCookie() ?? {}
  consent[consentKey(name)] = set ? 'granted' : 'denied'
  writeConsentCookie(consent)

  const updatedPreferences: CookiePreferences = {
    [name]: set
  }

  context.setCookiePreferences(updatedPreferences)
}

const syncPreferences = (context: CookieContextValue): void => {
  context.setCookiePreferences(consentToPreferences(readConsentCookie()))
}

interface CookiesManager {
  isBannerOpen: boolean;
  showBanner: (show: boolean) => void;
  cookiePreferences: CookiePreferences;
  setPreference: (name: CookiePreferenceName, value: boolean) => void;
  preferenceIsSet: (name: CookiePreferenceName) => boolean;
  hasPreference: (name: CookiePreferenceName) => boolean;
  hasEssentialPreference: () => boolean;
  hasAnalyticsPreference: () => boolean;
  hasPersonalisationPreference: () => boolean;
  clearPreferences: () => void;
  syncPreferences: () => void;
}

const useCookies = (): CookiesManager => {
  const context = useCookieContext()

  if (!context) {
    throw new Error('CookieContextProvider not present or has not initialised.')
  }

  return {
    isBannerOpen: context.isBannerOpen,
    showBanner: (show = true) => { context.setBannerOpen(show) },
    cookiePreferences: context.cookiePreferences,
    setPreference: (name, value) => { setPreference(context, name, value) },
    preferenceIsSet: (name) => {
      const consent = readConsentCookie()

      return consent !== null && consentKey(name) in consent
    },
    hasPreference: (name) => {
      const preference = getPreference(name)

      if (!preference) {
        return false
      }

      return hasPreference(preference)
    },
    hasEssentialPreference: () => hasPreference(cookiePreference.essential),
    hasAnalyticsPreference: () => hasPreference(cookiePreference.analytics),
    hasPersonalisationPreference: () => hasPreference(cookiePreference.personalisation),
    clearPreferences: () => {
      removeConsentCookie()
      context.setCookiePreferences(consentToPreferences(null))
    },
    syncPreferences: () => { syncPreferences(context) }
  }
}

export { cookiePreference, useCookies }

export type { CookiesManager }
